import * as React from 'react';
import { Loader2 } from 'lucide-react';
import { FormHandles, SubmitHandler } from '@unform/core';
import { Form } from '@unform/web';
import { Dialog, DialogContent } from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Select, Textarea } from 'components/Shared';
import { notify } from 'services/notifications';
import { useFetchViagensJustificationOptions } from 'hooks/api/viagens-controle';
import { useValidation } from 'hooks';
import type { SelectOption } from 'contracts/Common';
import * as Yup from 'yup';

type FormData = { justificationId: number | string; comment: string };

type Props = {
  billingId: number | null;
  loading?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: { justificationId: number; comment: string }) => void;
};

export const ViagensBillingEstornoDialog = ({ billingId, loading = false, onOpenChange, onSubmit }: Props) => {
  const formRef = React.useRef<FormHandles>(null);
  const { handleFormErrors } = useValidation();
  const { fetchViagensJustificationOptions } = useFetchViagensJustificationOptions();
  const [options, setOptions] = React.useState<SelectOption[]>([]);
  const [loadingOptions, setLoadingOptions] = React.useState(false);
  const open = billingId !== null;

  React.useEffect(() => {
    if (!open) return;
    setLoadingOptions(true);
    fetchViagensJustificationOptions('estadia-estorno')
      .then((items) => setOptions(items.map(({ id, description }) => ({ value: id, label: description }))))
      .catch((error: any) => notify('error', error?.message))
      .finally(() => setLoadingOptions(false));
  }, [fetchViagensJustificationOptions, open]);

  const handleSubmit = React.useCallback<SubmitHandler>(async (data: FormData) => {
    try {
      formRef.current?.setErrors({});
      const validData = await Yup.object({
        justificationId: Yup.number().typeError('Selecione uma justificativa').required('Selecione uma justificativa'),
        comment: Yup.string().trim(),
      }).validate(data, { abortEarly: false });
      onSubmit({ justificationId: Number(validData.justificationId), comment: validData.comment || '' });
    } catch (error) {
      handleFormErrors(error, formRef);
    }
  }, [handleFormErrors, onSubmit]);

  const close = React.useCallback(() => {
    if (loading) return;
    formRef.current?.reset();
    onOpenChange(false);
  }, [loading, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && close()}>
      <DialogContent className="max-w-[550px] bg-white p-6 sm:rounded-lg">
        <h2 className="font-GilroyBold text-xl text-slate-900">Estornar fatura</h2>
        <p className="mt-1 text-sm text-slate-600">Selecione o motivo do estorno. O tipo de justificativa é obrigatório.</p>
        <Form ref={formRef} onSubmit={handleSubmit}>
          <div className="mt-5 flex flex-col gap-5">
            <Select name="justificationId" label="Tipo de justificativa" placeholder="Selecione" options={options} isLoading={loadingOptions} isDisabled={loading} classContainer="min-h-[72px]" />
            <Textarea name="comment" label="Justificativa" placeholder="Digite uma observação (opcional)" rows={4} disabled={loading} classContainer="min-h-[160px]" />
            <div className="mt-2 flex gap-4">
              <Button type="button" variant="secondary" className="h-10 flex-1 bg-blue-50 font-GilroySemibold text-slate-900 hover:bg-blue-100" disabled={loading} onClick={close}>Cancelar</Button>
              <Button type="submit" className="h-10 flex-1 bg-red-600 font-GilroySemibold text-white hover:bg-red-700" disabled={loading}>
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Salvando...</> : 'Estornar fatura'}
              </Button>
            </div>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
